import React from 'react'
// If ANYTHING here changes, update tests in help.test.jsx

/**
 * This function is called by the help component to display appropriate help
 * messages.
 * @param  {[string]} id string used to identify the help button clicked
 *                       so that an appropriate message can be selected.
 * @return {[String]}    Returns the help string associated with the given id.
 */
export default function getHelp(id) {
  switch (id) {
    /*
| Begin injection workflow window help
*/

    case 'SYSTEM_OVERVIEW_LATENCY':
      return (
        <div className="formatted-help">
          <p>
            <b>Overview</b>
            <br />
            This panel answers “is the app slow right now, and where?” using the
            server’s response times over the window you pick. Short windows (15m
            / 30m / 1h) show full percentile detail; longer ones (6h / 24h / 7d)
            show averages — handy when a problem may have been going on for a
            while.
          </p>
          <p>
            <b>Reading the numbers</b>
            <br />
            <b>p50 / p95 / p99</b> — response times in ms: p50 is the typical
            request, p95 the slowest 5%, p99 the slowest 1%.
            <br />
            <b>avg / 24h avg</b> — the window’s average and the last-day average
            (the “normal” to compare against).
            <br />
            <b>requests / error rate</b> — how many requests, and the % that
            returned a server error.
            <br />
            <b>client p95 / network p95</b> — measured in the browser; network
            is the time spent in transit. Server tiles carry a server icon,
            client and network their own.
          </p>
          <p>
            <b>Top slow endpoints</b>
            <br />
            The slowest actions in the window. When a baseline is shown it is
            that endpoint’s normal p95; a value far above baseline is slower
            than usual and worth escalating. Use the show 10 / 25 / 50 control
            to list more.
          </p>
          <p>
            <b>Troubleshooting</b>
            <br />
            High p95/p99 with a normal p50 → some requests are stalling. Window
            avg well above the 24h avg → slower than normal right now. High
            network p95 with low server numbers → the connection, not the app.
            The Insights box flags these automatically, and Run latency probe
            times a few requests from your browser right now.
          </p>
        </div>
      )
    case 'ACTIVATEVIALS_WIDGET':
      return 'Allows you to activate individual vials for the selected patient. \n Vials must be activated vials before they are available for injection.'
    case 'EXTRACTS_WIDGET':
      return 'Manage all defined extracts and diluents that are available for prescribing and testing.'
    case 'IMMUNOTHERAPYSUMMARY_WIDGET':
      return "This is a summary of the selected patient's immunotherapy history."
    case 'ORDERENTRY_WIDGET':
      return 'Create, view, or edit a prescription order for the selected patient. \n A single order may contain multiple prescriptions. \n Each prescription can then be mixed one or more times to create treatment sets.'
    case 'UPDATEPATIENT_WIDGET':
      return "Edit the selected patient's demographic and contact information."
    case 'PURCHASEORDERS_WIDGET':
      return 'A table of all prescription orders for the selected patient.'
    case 'TREATMENTSETS_WIDGET':
      return 'A table of all treatment sets for the selected patient. \n By default when viewed outside of the patient context, this window filters to only show treatment sets that were created "this year".'
    case 'SKINTESTHISTORY_WIDGET':
      return "The history of skin tests that have been performed on the current patient. \n Click on the test's date to see full details."
    case 'SKINTESTDETAIL_WIDGET':
      return 'Results of a skin test performed on the selected patient.'
    case 'SKINTESTLIST_WIDGET':
      return 'A table of all skin tests that have been performed in the current context.\n By default when viewed outside of the patient context, this window filters to only show skin tests that were created "this year".'
    case 'PATIENT_NOTE_WIDGET':
      return 'This window shows any notes written about the patient’s care. \n Check the box if you would like the patient note to popup whenever the patient’s chart is accessed.'
    case 'PRESCRIPTIONS_WIDGET':
      return 'This window shows all prescriptions in the system that are associated with the selected patient.'
    case 'EXPRESS_INJECTION_WIDGET':
      return 'This window allows you to quickly record injection administration from multiple vials.'
    case 'QUESTIONNAIRE_WIDGET':
      return 'This window shows the pre-shot questionnaire that the patient must complete prior to receiving an injection. \n It may be the case that certain responses disqualify the patient from receiving an injection today (for example, if they currently have a bad bout of the flu). These cases are defined by your organization.'
    case 'QUESTIONNAIRE_REVIEW_WIDGET':
      return 'This window shows the completed pre-shot questionnaire with the patient’s responses.'
    case 'INJECTION_GRAPH_WIDGET':
      return 'This window shows a graphical screen of the patient’s injection history for the selected prescription. \n Hover over each point on the graph to see more information about that visit. A circular point indicates a plan-based dose; a square point indicates an adjusted dose. \n The line colors represent the vial colors that the injection was given from.'
    case 'STANDARD_INJECTION_WIDGET':
      return 'This window shows all prescriptions available that patient can receive an injection from. \n Use the red X to remove an injection from today’s visit. Any white areas may be manually adjusted. \n Click Activate Vial to see and select the available vials for that prescription.'
    case 'INJECTION_WIDGET':
      return 'This window shows all injections in the system that are associated with the selected patient or prescription.'
    case 'ACTIVATE_VIALS_WIDGET':
      return 'This window shows all available vials for the selected prescription. Select a vial and use the single arrows to activate or deactivate it. Use the double arrows to activate or deactivate all vials.'
    case 'WRAPUP_WIDGET':
      return 'This window shows the final actions needed to complete the patient’s visit. Select whichever actions are appropriate, then click Next.'
    case 'SKINTEST_WIDGET':
      return 'This window shows all skin tests in the system that are associated with the selected patient or prescription. \n By default this window filters to only show skin tests that were created "this year".'
    case 'INJECTION_ADJUST_WIDGET':
      return 'This window lets you adjust the next injection dose(s) for a given prescription. These doses will be prompted during the injection workflow at the Injection step. \n Select the prescription number from the drop-down menu. Key in the dose and date this dose should be administered. Include a reason for manually adjusting the dose.'
    case 'INJECTIONS_WIDGET':
      return 'This window shows you a complete history of all injections administered for the patient. \n Click on an injection row to bring up more details, add reaction information, or key in notes. \n By default this window filters to only show injections that were administered "this year".'
    case 'INVENTORY_WIDGET':
      return 'A table of all extracts in current inventory. \n You can add new bottles and edit and delete existing ones.'
    case 'TRACKING_WIDGET':
      return 'Use this window to record biometric data for the patient, including their blood pressure readings or peak flow / FeNO results. Hide or show specific biometrics by clicking on the colored dots top center.'
    case 'NON_XIS_INJECTION_WIDGET':
      return 'Use this window to manually enter any injections that were not given using the Injection window. Use cases for this include pre-loading existing patient treatment when moving from existing systems to Xtract or as part of a downtime procedure.'
    case 'NON_XPS_PRESCRIPTIONS_WIDGET':
      return 'Use this window to manually enter any prescriptions that were not created or mixed with Xtract. Use cases for this include: \n Pre-loading existing patient prescriptions when moving from existing systems to Xtract. \n Using Xtract to administer immunotherapy prescriptions for venoms, biologics, and flu shots. \n Using Xtract to administer immunotherapy mixed at a lab or by another organization.'
    case 'QUESTIONNAIREHISTORY_WIDGET':
      return "The selected patient's history of completing questionnaires."
    case 'VIALSLIST_WIDGET':
      return 'A table of all vials that have been made for the selected context. \n By default when viewed outside of the patient context, this window filters to only show vials that were created "this year".'
    case 'VIALTESTSCORING_WIDGET':
      return 'List of all vials that are currently being tested for the selected patient.\n Vial testing serves as a biologic indicator of tolerance to the mixed antigen vial.'
    case 'MIXINGQUEUE_WIDGET':
      return 'Queue of treatment sets that are ready to mix for all patients.\n By default this window filters to only show treatment sets that were created "this year".'
    case 'VIALMIXING_WIDGET':
      return 'Queue of treatment sets that are ready to mix for the selected patient.'
    /*
    | Begin patient windows help
    */
    case 'UPDATE_PATIENT_WIDGET':
      return 'This window displays the demographic and contract information for the patient. This is also where you enroll their fingerprints, add their PIN, and assign Questionnaires to them.'
    case 'DEMOGRAPHICS_WIDGET':
      return 'This window provides a summary of the patients immunotherapy treatment. Any injections that results in a local or systemic reaction are shown at the top of the window. Other information displayed here includes: \n Their compliance score, which shows how many injections they received on schedule. \n The count of days where a local or systemic reaction was recorded. \n Important dates in their treatment, such as their last provider visit and when they began treatment. \n A list of codes applied to them and the descriptions for those codes.'
    /*
    | Begin dashboard window help
    */
    case 'EXCUSE_WIDGET':
      return 'This window shows patients who have already received their injections and are waiting to be excused. \n Click the *three dots symbol* to edit the information displayed here. \n Use Action dropdown to select the next step once patient has waited their time.'
    case 'INJECTION_WAITING_WIDGET':
      return 'This window shows patients who have logged in but are still waiting to be seen. \n Click the *three dots symbol* to edit the information displayed here.'
    case 'ROOMED_WIDGET':
      return 'This window shows patients who have been shown to a room and are with injection staff.'
    /*
    | Begin settings/injection help
    */
    case 'TREATMENT_PLAN_NAME':
      return 'Name the treatment build up plan here. For example: “Standard Build Up” or “0.5mL Maintenance”.'
    case 'MAINT_STEPS_BACK':
      return 'Indicate the number of steps back when starting a patient on a new maintenance vial. For example, “-2” would tell the system to suggest a dose two steps back from the last dose in their maintenance vial.'
    case 'FOLD':
      return 'Use the dropdown menu to set the treatment plan as a 10-fold (1:1, 1:10, 1:100...) or 5-fold (1:1, 1:5, 1:25...) dilution.'
    case 'DOSING_PLAN':
      return 'Use the dropdown menu to select the dosing plan to apply to this treatment plan. The dosing plans will offer dose adjustments in the case of a severe reaction, overdue patient, etc.'
    /*
    | Begin General local help
    */
    case 'CLINIC_LOCATION':
      return 'Clinic location is used to ensure that if an organization operates multiple clinics, the various Xtract dashboards will only display patients at the relevant clinic.'
    case 'CLOCK_UPDATE_FREQUENCY':
      return 'This is the number of seconds between clock ticks. On slow systems it may be necessary to change this so that the clock updates less frequently (eg. every 30s)'
    case 'LIST_UPDATE_FREQUENCY':
      return 'The length of time (in seconds) the app should wait to fetch updated patient lists from the server. Systems with slow internet connections may benefit from increasing this value.'
    case 'PAGE_SIZE':
      return 'This is the number of rows of patients that are visible in the patient list. Generally this should not be changed but may be used to help the application display better at non standard resolutions or orientations.'
    case 'SHOW_SECONDS':
      return 'This setting determines if the number of seconds will be visible on patient wait times or if only hours and minutes should be visible'
    case 'HIGHLIGHT_WAITING_TO_BE_EXCUSED':
      return 'If set, patients will be highlighted in red after they have been waiting to be excused for this number of minutes. \n Setting this value to -1 will result in no patients ever being highlighted in red.'
    case 'HIGHLIGHT_WAITING_FOR_INJECTION':
      return 'If set, patients will be highlighted in red after they have been waiting for an injection for this number of minutes. \n Setting this value to -1 will result in no patients ever being highlighted in red.'
    case 'HIGHLIGHT_WITH_STAFF':
      return 'If set, patients on the rooming display will be highlighted in red after this number of minutes with staff. Settings this value to -1 will result in no patients ever being highlighted in red.'
    case 'PAGE_FLIP_TIME':
      return 'When there are too many patients on the list to be displayed at the same time, the app will cycle between "pages" of patients. This is the number of seconds between "page" changes.'
    /*
    | Begin General > Global Settings help
    */
    case 'DEFAULT_EXCUSE_TIME':
      return 'This is the default time for adding a patient to the Waiting to be Excused window'
    case 'LOGO_IMAGE*':
      return 'This logo image will display on the Summit login page and the lobby dashboard'
    case 'INACTIVITY_TIMEOUT_(MINS)':
      return 'This setting controls how long, in minutes, a user can be inactive in Summit before being automatically logged out. When this amount of time has passed, an Inactivity Alert message will instruct the user that they have 60 seconds to indicate they are still there (for example, click the mouse) before being logged out.'
    case 'MAX_STEP_ALERT':
      return "This setting tells the system what the largest acceptable dose increment is. Use a two-digit value with a decimal, for example .25 or 1.0. Any dose jumps larger than this increment will trigger a warning to appear, although it can be clicked through if the dose is indeed appropriate. \n  \n For example, a MaxStepAlert set at 0.75 would mean that if a patient was given .25mL one day, and then 1.25mL was adjusted in for the next shot, a warning would indicate the MaxStepAlert had been hit to confirm the user didn't mistype."
    case 'UPDATE_ADDRESS_HELP':
      return "The addresses were originally linked during creation. If you do not want to update the other address(es), exit the modal and click 'Replace' to change just this address."
    case 'VIAL_SIZE':
      return 'This list of vial sizes will display when creating prescribing profiles, which are used for patient treatment set creation. Standard vial sizes are 5mL and 10mL but you may add others that you use.'
    case 'DEFAULT_COUNTRY':
      return 'This will set the default country which appears in an address or phone input throughout the app'
    case 'LOBBY_PATIENT_NAME_FORMAT':
      return 'This will set how patient names are displayed in the lobby dashboard'
    case 'USE_LOBBY_DISPLAYNAME':
      return 'When on, patient display name will be used. If not available, selected lobby patient name format would be used instead.'

    /*
    | Begin General > Status Settings help
    */
    case 'STATUS_NAME':
      return 'Statuses are shown in drop down menus throughout the application, such as in the Mix Queue, Order Queue, and during Order Entry. The status name is the text that shows, for example "Pending Review" or "Ready to Mix". You can create any status names that are appropriate for your clinical environment.'
    case 'STATUS_POSITION':
      return 'The position value determines the order that the status names will display. The position value is also what is used in the database to link status names to prescriptions, orders, and treatment sets. Caution: Changing the position of a status name will cause anything assigned to it to now show the status name of whatever replaces the original position value.'
    /*
    | Begin General > Attachment Tags Settings help
    */
    case 'TAG_NAME':
      return 'Attachment tags can be applied to patient attachments from the attachment modal. The tag name is the text that shows, for example "Treatment Consent" or "Billing Authorization". You can create any attachment tag names that are appropriate for your clinical environment.'
    case 'TAG_POSITION':
      return 'The position value determines the order that the attachment tag names will display in the attachment tag menu.'
    /*
    | Begin General > Text Summary help
    */
    case 'INJECTION_SUMMARY':
      return 'If configured, you can see what the Injection Summary looks like by supplying the required info then clicking Test Summary'
    case 'MIX_SUMMARY':
      return 'If configured, you can see what the Mix Summary looks like by supplying the required info then clicking Test Summary'
    case 'SKINTEST_SUMMARY':
      return 'If configured, you can see what the Skintest Summary looks like by supplying the required info then clicking Test Summary'
    /*
    | Begin login settings help
    */
    case 'SHOW_QUESTIONNAIRE':
      return 'Turn this setting ON to display the questionnaire when a patient signs in using the kiosk.'
    case 'HEADER_TEXT':
      return 'This text will display on the kiosk main page.'
    case 'ALERT_MESSAGE_TIMEOUT_(SEC)':
      return 'Set the number of seconds a user can leave the screen inactive before it times out. Must be a minimum 10 seconds.' //OR 'Set the number of seconds that an alert will stay on the screen before timing out' OR 'Set the number of seconds that a user...
    case 'REQUIRE_COMPLETE_QUESTIONNAIRE':
      return 'Turn this setting ON to require the patient to complete their pre-shot questionnaire on the kiosk. Note that the Show Questionnaire setting must be turned ON.'
    case 'ALLOW_PATIENT_SIGNOUT':
      return 'Turn this setting ON to allow the patient to sign themselves out using the kiosk.'
    case 'REQUIRE_FULL_WAIT_TIME':
      return 'Turn this setting ON to disallow the patient from signing themselves out before their post-injection wait time has elapsed. Note that Allow Patient Signout setting must be ON.'
    case 'ALLOW_MANUAL_LOGIN':
      return 'Turn this setting ON to allow patients to manually enter their name when signing in. Note that front desk or nursing staff will need to link patient account if one already exists. \n Turn this setting OFF to require patients to search for their existing record in the system. Note that if the patient cannot locate their record, they will need assistance from the front desk.'
    case 'LOCK_ON_BOX3':
      return 'This setting will prevent any patients assigned to the configured box (default: box 3) from logging in through the kiosk, instead they will be forced to the front desk.'
    case 'BOX_3_LOCKOUT_MESSAGE':
      return "When Lock on the configured box (default: box 3) is enabled, this is the message shown to the patient on the kiosk when they are prevented from signing in. If left blank, the default message will be used: 'Please see front desk staff to complete login process.'"
    case 'PIN_TO_EXIT_FULL_SCREEN':
      return 'This is the 4 digit pin required to exit full screen in the kiosk.'
    case 'BACKGROUND_IMAGE':
      return 'This image will be disaplyed as the kiosk background.'
    /*
    | Begin Dashboard settings help
    */
    case 'SLIDE_DURATION_(MS)':
      return 'This setting controls how long each slide is displayed during the slideshow, in milliseconds. For example, a value of 5000 means each slide will be shown for 5 seconds.'
    case 'SLIDE_REPEAT_TIME_(MS)':
      return 'This setting controls how often the slideshow restarts after it has completed a full cycle through all slides, in milliseconds. For example, a value of 60000 means the slideshow will restart every 60 seconds after completing a full loop through all slides.'
    case 'SLIDE_FADE_TIME_(MS)':
      return 'This setting controls how long the fade transition between slides takes, in milliseconds. For example, a value of 1000 means the fade transition will take 1 second.'
    case 'ENABLE_SLIDESHOW':
      return 'Turn this setting ON to enable the slideshow feature on the lobby dashboard.'
    /*
    | Begin user management help
    */
    case 'PRIVILEGE':
      return 'There are several privilege levels available in the system. \n An Admin can perform all tasks and has advanced control over settings and preference menus. \n A User can… \n A LiteUser can...'
    case 'DISPLAY_NAME':
      return 'This is the name that the user will use to log into the system. Additionally, it is the name that the system will use when referring to the user.'
    case 'GENERAL_NOTES':
      return 'This field allows a general note of up to 150 characters. This note may be used for internal purposes such as tracking user IDs within EMR/EHRs. The note does not display in the Xtract system but can appear in messages across the interface.'
    case 'VISIBLE_TEMPLATES':
      return 'Select which templates the user can choose from when printing from this dialog.'
    case 'GLOBAL_DEFAULT_SETTINGS':
      return 'Select the default documents to be printed (and on which printers) when printing from this dialog.'
    /*
    | Begin pharmacy help
    */
    case 'PHARMACY_NAME':
      return 'Name of the pharmacy as it should appear in the user interface.'
    case 'PHARMACY_TYPE':
      return 'The type of the pharmacy. \n A Local pharmacy maintains a mix queue for compounding vials. All installations will have a Local phamacy. \n A Non-Compounding pharmacy is an external source that provides mixed vials of indeterminate orgin. Also used for biologics and venoms.'
    case 'STATE_MACHINE':
      return 'The name of the State Machine that controls how treatment sets move through the ordering and mixing process.'
    case 'IS_ACCEPTING_ORDERS?':
      return 'Flag that controls whether or not the pharmacy is currently accepting orders.'
    // case 'CLIENT_NAME':
    //   return ''
    case 'FONT_AWESOME_ICON':
      return 'Choose a FontAwesome icon family and icon name. \n Names can be found at https://fontawesome.com/v5/cheatsheet and https://fontawesome.com/v5/cheatsheet/free/regular.'
    case 'PHARMACY_POSITION':
      return 'The position value determines the order that the pharmacy names will display.'
    /*
    | Begin Customer Export help
    */
    case 'CUSTOMER_EXPORT':
      return (
        <div className="formatted-help">
          <p>
            <b>Overview</b>
            <br />
            Customer Export copies patient records non-destructively to the
            configured export bucket (S3). The source data is never modified or
            deleted; re-running skips already-exported files so it is safe to
            resume after a failure.
          </p>
          <p>
            <b>When to use</b>
            <br />
            Export is only available while <b>Import In Progress</b> is enabled.
            That flag puts the site in a maintenance state intended for
            controlled data-migration windows.
          </p>
          <p>
            <b>Output layout</b>
            <br />
            Each run writes into a timestamped folder,
            e.g. <code>export/2026-06-18_142233/</code>. CSVs land at the run
            root; reports and attachments are organized under
            <code>patient/&lt;id&gt;/reports/</code> and
            <code>patient/&lt;id&gt;/attachments/</code>.
          </p>
        </div>
      )
    case 'CUSTOMER_EXPORT_PDF_REPORTS':
      return 'Export reports whose template renders to a PDF. These are typically standard clinical report types such as prescription summaries and treatment histories.'
    case 'CUSTOMER_EXPORT_TEXT_REPORTS':
      return 'Export reports whose template renders to plain text. Selecting this alongside PDF reports will export both types in the same run.'
    case 'CUSTOMER_EXPORT_ATTACHMENTS':
      return (
        <div className="formatted-help">
          <p>
            Export all patient attachments — uploaded files of any type (PDFs, images, Word documents, etc.) that have been associated with patient records.
          </p>
          <p>
            Note: All attachments are already stored in the s3 bucket /attachments directory.
            Including this in the export will simply copy the attachments into the export directory structure and properly disperse them into the individual patient directories.
            If there is a need to save time, this could be skipped and a copy of the attachments directory could simply be downloaded and included with the other exports.
          </p>
        </div>
      )
    case 'CUSTOMER_EXPORT_PATIENT_DATA_CSV':
      return (
        <div className="formatted-help">
          <p>
            Export patient data as a set of CSV files: patient demographics,
            prescriptions, vials, injections, skin tests, and related records.
            Each table is written as its own CSV at the run root.
          </p>
          <p>
            <b>Note:</b> CSV export requires a database privilege
            (<code>CREATE PROCEDURE</code>) that the standard application user
            may not have. Contact Xtract Solutions support if CSVs are needed
            and the export reports a failure for this step.
          </p>
        </div>
      )
    case 'CUSTOMER_EXPORT_INCLUDE_FRIENDLY_FILE_NAMES':
      return 'Include the human-readable report or attachment name in each exported filename, e.g. "42_Injection Summary.pdf" instead of "42.pdf". Produces more legible output but is slower because it runs each name through the template engine. Leave off for large datasets if speed matters.'
    /*
    | No message found
    */
    default:
      return `No help message available. Please contact Xtract Solutions Support with any questions.\n${id}`
  }
}
